import { Language } from '@zond/api-interfaces';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useUpdateUserProfile } from '../modules/users/user.queries';
import i18n from './config';
import { getStoredLanguage, setStoredLanguage } from './language';

interface LanguageContextValue {
  /** Currently active UI language. */
  language: Language;
  /** User-initiated change: applies instantly and persists to the profile. */
  setLanguage: (language: Language) => void;
  /** Sync from the server profile: applies instantly, does NOT re-persist. */
  applyServerLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: getStoredLanguage(),
  setLanguage: () => undefined,
  applyServerLanguage: () => undefined,
});

const isAuthenticated = () => {
  try {
    return Boolean(localStorage.getItem('accessToken'));
  } catch {
    return false;
  }
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => getStoredLanguage());
  const updateProfile = useUpdateUserProfile();
  // Keep a live ref to `mutate` so the callbacks below can stay referentially
  // stable — otherwise consumers (e.g. the layout server-sync effect) re-run on
  // every language change and can fight the user's selection.
  const mutateRef = useRef(updateProfile.mutate);
  mutateRef.current = updateProfile.mutate;

  const applyLocal = useCallback((next: Language) => {
    setStoredLanguage(next);
    if (i18n.language !== next) {
      i18n.changeLanguage(next);
    }
    setLanguageState((prev) => (prev === next ? prev : next));
  }, []);

  const setLanguage = useCallback(
    (next: Language) => {
      applyLocal(next);
      if (isAuthenticated()) {
        // Best-effort persistence; the switcher already applied the change locally.
        mutateRef.current({ language: next });
      }
    },
    [applyLocal]
  );

  const applyServerLanguage = useCallback(
    (next: Language) => {
      applyLocal(next);
    },
    [applyLocal]
  );

  const value = useMemo(
    () => ({ language, setLanguage, applyServerLanguage }),
    [language, setLanguage, applyServerLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
